import {
  __federation_method_setRemote,
  __federation_method_getRemote,
  __federation_method_unwrapDefault,
} from 'virtual:__federation__';
import type { IPluginLoader } from '@/application/ports/IPluginLoader';
import type { ActivationPlugin } from '@hub/plugin-sdk';
import type { PluginRemoteConfig } from '@/domain/entities/PluginRemoteConfig';

const REMOTE_ENTRY_PATH = '/assets/remoteEntry.js';

/**
 * Carrega plugins remotos dinamicamente via Module Federation.
 *
 * Usa o módulo virtual `virtual:__federation__` do
 * @originjs/vite-plugin-federation para registrar o remoto em runtime (URL
 * resolvida via `GET /plugin-registry/{type}`, HUB-237 — conectada ao
 * `ActivationFlow` nesta fatia) e obter o módulo exposto `./ActivationPlugin`.
 *
 * `config.version` é anexado como query string (`?v=<version>`) à URL do
 * `remoteEntry.js` para cache-busting (HUB-237, ver `PluginRemoteConfig`).
 *
 * React e React-DOM são `shared` na config do host e dos plugins, garantindo
 * uma única instância de React e ausência de contexto duplicado.
 */
export class FederationPluginLoader implements IPluginLoader {
  async load(config: PluginRemoteConfig): Promise<ActivationPlugin> {
    __federation_method_setRemote(config.id, {
      url: () =>
        Promise.resolve(
          `${config.remoteUrl}${REMOTE_ENTRY_PATH}?v=${encodeURIComponent(config.version)}`
        ),
      format: 'esm',
      from: 'vite',
    });

    const exposed = await __federation_method_getRemote(config.id, config.moduleName);
    const plugin = __federation_method_unwrapDefault(exposed) as ActivationPlugin;

    if (!plugin || !plugin.id || !plugin.Component) {
      throw new Error(
        `Plugin "${config.id}" não implementa o contrato ActivationPlugin corretamente.`
      );
    }

    return plugin;
  }
}
